import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const PLACEHOLDER_LEGEND_COUNT = 6;

export const BranchComparisonSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.branchComparison.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="branch-comparison-widget-title"
            {...getNodeTestIdProp('branch-comparison-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('branch-comparison-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('branch-comparison-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('branch-comparison-widget-title')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'branch-comparison-icon-skeleton',
                            )}
                        />
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'branch-comparison-widget-subtitle',
                        )}
                    >
                        <Skeleton
                            className="h-3 w-40 rounded-md"
                            {...getNodeTestIdProp(
                                'branch-comparison-widget-subtitle-skeleton',
                            )}
                        />
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content>
                    <ul
                        className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2"
                        {...getNodeTestIdProp('branch-comparison-legend')}
                    >
                        {Array.from({ length: PLACEHOLDER_LEGEND_COUNT }).map(
                            (_, index) => (
                                <li
                                    key={index}
                                    className="flex flex-col gap-3 rounded-xl bg-default/40 p-4"
                                    {...getNodeTestIdProp(
                                        `branch-comparison-item-placeholder-${index}`,
                                    )}
                                >
                                    <div
                                        className="flex items-center justify-between gap-3"
                                        {...getNodeTestIdProp(
                                            `branch-comparison-item-header-placeholder-${index}`,
                                        )}
                                    >
                                        <div
                                            className="flex min-w-0 items-center gap-2"
                                            {...getNodeTestIdProp(
                                                `branch-comparison-item-label-root-placeholder-${index}`,
                                            )}
                                        >
                                            <Skeleton
                                                className="size-2.5 shrink-0 rounded-sm"
                                                {...getNodeTestIdProp(
                                                    `branch-comparison-item-swatch-placeholder-${index}`,
                                                )}
                                            />
                                            <Skeleton
                                                className="h-3 w-32 rounded-md"
                                                {...getNodeTestIdProp(
                                                    `branch-comparison-item-name-placeholder-${index}`,
                                                )}
                                            />
                                        </div>
                                        <Skeleton
                                            className="h-3.5 w-16 shrink-0 rounded-md"
                                            {...getNodeTestIdProp(
                                                `branch-comparison-item-revenue-placeholder-${index}`,
                                            )}
                                        />
                                    </div>
                                    <Skeleton
                                        className="h-3 w-full rounded-full"
                                        {...getNodeTestIdProp(
                                            `branch-comparison-item-progress-placeholder-${index}`,
                                        )}
                                    />
                                </li>
                            ),
                        )}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
